import React, { useRef, useState } from 'react';

import { Button, Checkbox, ChoiceTile, DropdownMenu, Input, ScrollBar, SparkIcon, Text } from '@spark';

import { COMPANY_SIZE_OPTIONS, HEARD_ABOUT_OPTIONS, INDUSTRY_OPTIONS, ROLE_OPTIONS } from '@/shared/constants/profileOptions';

import { createWorkspace, updateProfile } from '../api/workspacesApi';
import { PRIVACY_POLICY_URL, TERMS_URL } from '../content/legalLinks';
import onboardingTerms from '../content/onboardingTerms.txt?raw';
import { COUNTRIES, countryCodeForName, countryName } from '../utils/countries';
import { cropImageToSquare, logoImageErrorText } from '../utils/logoImage';
import { sanitizeWorkspaceName } from '../utils/workspaceName';
import useLogoCrop from '../hooks/useLogoCrop';
import LogoCropModal from './LogoCropModal';
import OnboardingLayout from './OnboardingLayout';
import WorkspaceDetailsFields from './WorkspaceDetailsFields';

/**
 * All wizard steps, in order (Figma "MVP-DESIGNES" 9224:4381 / 9358:1189,
 * 9224:4435, 9224:4431, 9226:4449, 9236:2369).  `completed_step` on the user
 * document counts finished steps 1..5 in *this* numbering, even when the
 * wizard shows a subset (see `steps` on `CreateWorkspaceWizard`), so a
 * resumed user is placed by `resolveInitialStep` regardless of which steps
 * were visible when they saved.  The `goals` card holds both "What do you
 * want to improve" and "Who is the model for?" (one screen in Figma).
 */
export const ONBOARDING_STEPS = Object.freeze([
    { id: 'about', title: 'Tell us about yourself', artwork: 'about' },
    { id: 'workspace', title: 'Define your workspace', artwork: 'workspace' },
    { id: 'goals', title: 'What do you want to improve with Specific AI?', artwork: 'improve' },
    { id: 'heard_about', title: 'How did you hear about us?', artwork: 'audience' },
    { id: 'terms', title: 'Review our terms', subtitle: 'Agree to the terms to continue', variant: 'wide' },
]);

export const CREATE_WORKSPACE_STEP_COUNT = ONBOARDING_STEPS.length;

/** Heading of the second tile group on the goals card (Figma 9224:4431). */
export const MODEL_AUDIENCE_TITLE = 'Who is the model for?';

/**
 * The single-select fields of "Tell us about yourself" (Figma 9358:1189, in
 * card order between Full Name and From).  Each is shown only when the
 * answer is still unknown when the wizard mounts — the waiting list already
 * asked these questions and `POST /users/bootstrap` copies the approved
 * request's answers into `onboarding`, so most creators see the short card
 * (Figma 9224:4381).  Values are the option labels, validated by the backend
 * against the same lists.
 */
export const PROFILE_SELECT_FIELDS = Object.freeze([
    { key: 'role', label: 'Role', placeholder: 'Select Role', options: ROLE_OPTIONS, error: 'Please select your role.' },
    {
        key: 'company_size',
        label: 'Company Size',
        placeholder: 'Select Size',
        options: COMPANY_SIZE_OPTIONS,
        error: 'Please select your company size.',
    },
    {
        key: 'industry',
        label: 'Industry',
        placeholder: 'Select Industry',
        options: INDUSTRY_OPTIONS,
        error: 'Please select your industry.',
    },
]);

const cleanAnswer = (value) => (typeof value === 'string' ? value.trim() : '');

/** `value` when it is one of `field`'s option labels, else `''`. */
const knownOption = (field, value) => (field.options.includes(cleanAnswer(value)) ? cleanAnswer(value) : '');

/**
 * The select fields a wizard mounted with `initialProfile` must still ask:
 * those whose saved answer is empty (or not one of the known labels).
 */
export const missingProfileSelects = (initialProfile) =>
    PROFILE_SELECT_FIELDS.filter((field) => !knownOption(field, initialProfile?.[field.key]));

/** 1-based position of a step id in `ONBOARDING_STEPS` — the value stored as `completed_step`. */
export const completedStepFor = (stepId) => ONBOARDING_STEPS.findIndex((step) => step.id === stepId) + 1;

/**
 * Steps that get a segment in the progress indicator (Figma 9251:5312 ..
 * 9237:3395): the split screens only — the terms screen has no indicator.
 */
export const progressSegmentsFor = (steps) => steps.filter((step) => step.id !== 'terms');

export const CONSENT_ITEMS = Object.freeze([
    { id: 'terms', label: 'I agree to the Terms & Conditions', linkLabel: 'Terms & Conditions', url: TERMS_URL },
    { id: 'privacy', label: 'I agree to the Privacy Policy', linkLabel: 'Privacy Policy', url: PRIVACY_POLICY_URL },
]);

/** Multi-select answers for "What do you want to improve with Specific AI?" (stored verbatim). */
export const IMPROVE_GOAL_OPTIONS = Object.freeze([
    'Reduce model costs',
    'Improve response speed',
    'Keep data private',
    'Improve task-specific accuracy',
    "Explore what's possible",
]);

/** Multi-select answers for "Who is the model for?" (stored verbatim). */
export const MODEL_AUDIENCE_OPTIONS = Object.freeze([
    'A customer-facing product',
    'An internal tool',
    'Research/experimentation',
    'A personal project',
]);

export const ONBOARDING_TERMS_TEXT = onboardingTerms;

/** Country rows carry the same world-map glyph as the trigger (Figma 9224:4381), not the Dropdown default person icon. */
const COUNTRY_ICON_SLUG = 'world-map';
const COUNTRY_ITEMS = COUNTRIES.map((country) => ({
    label: country.name,
    searchText: country.name,
    prefixIconSlug: COUNTRY_ICON_SLUG,
}));

const clampStep = (value, steps) => {
    const step = Number.isFinite(Number(value)) ? Math.trunc(Number(value)) : 0;
    return Math.min(Math.max(step, 0), steps.length - 1);
};

const toggleValue = (list, value) => (list.includes(value) ? list.filter((item) => item !== value) : [...list, value]);

/**
 * Segmented step indicator at the bottom of the left column (Figma
 * 9251:5312): one 6px pill per split-screen step, filled up to and including
 * the current one.  Not rendered on the terms screen.
 */
function OnboardingProgress({ segments, position }) {
    if (!segments.length || position < 1) return null;
    return (
        <div
            className="onboarding-progress"
            role="progressbar"
            aria-label="Onboarding progress"
            aria-valuemin={1}
            aria-valuemax={segments.length}
            aria-valuenow={position}
            aria-valuetext={`Step ${position} of ${segments.length}`}
        >
            {segments.map((segment, index) => (
                <span
                    key={segment.id}
                    className={`onboarding-progress-segment${index < position ? ' onboarding-progress-segment-active' : ''}`}
                />
            ))}
        </div>
    );
}

/**
 * One consent row on the terms screen: a required Spark checkbox with the
 * exact Figma label, plus a "Read ..." link to the document (`legalLinks.js`
 * → the in-app PDF viewer, opened in a new tab). An empty URL would render
 * only the label.
 */
function ConsentCheckbox({ item, checked, disabled, onToggle }) {
    return (
        <div className="onboarding-consent-row">
            <Checkbox
                id={`onboarding-consent-${item.id}`}
                name={`consent_${item.id}`}
                label={item.label}
                status={checked ? 'active' : 'inactive'}
                disabled={disabled}
                required
                onChange={onToggle}
            />
            {item.url ? (
                <a className="onboarding-consent-link" href={item.url} target="_blank" rel="noopener noreferrer">
                    Read the {item.linkLabel}
                </a>
            ) : null}
        </div>
    );
}

/**
 * Creator / invitee onboarding (`/create-workspace`), rendered on
 * `OnboardingLayout`.  `steps` is the ordered subset of `ONBOARDING_STEPS` to
 * show (all five by default; `CreateWorkspacePage` drops "Define your
 * workspace" for anyone who already belongs to a workspace).  Every "Next"
 * persists that step through `POST /users/set-profile` with `completed_step`
 * in the global 1..5 numbering, so the user resumes at the same point after a
 * reload or re-login:
 *
 * 1. Tell us about yourself — full name, company name and country are always
 *    asked (all required).  Role, company size and industry (Figma 9358:1189)
 *    appear only for the answers missing from `initialProfile` — the approved
 *    waiting-list request usually supplied them — and are then required too.
 *    The company name is kept for the workspace step.
 * 2. Define your workspace — optional company logo, workspace name (required).
 *    `POST /workspaces` unless the user already owns one (`existingWorkspace`),
 *    in which case the name and logo are shown read-only.
 * 3. What do you want to improve with Specific AI? + Who is the model for? —
 *    two multi-select groups on one card, >= 1 each.
 * 4. How did you hear about us? — single-select, required.
 * 5. Review our terms — both consent checkboxes are required; "Continue to
 *    Specific" is enabled once both are ticked, stamps `terms_accepted_at`
 *    (once) and hands the workspace to `onCreated` (the page reloads to `/`).
 *
 * `initialProfile` is `{ role, company, company_size, industry }` from
 * `user.onboarding`; `initialCompany` is the fallback for the company name
 * (derived from the email domain) when none was saved yet.  `defaultProfile`
 * is `{ company, company_size, industry }` an invitee inherits from the
 * workspace owner: unlike `initialProfile` it only pre-fills — the fields stay
 * visible and editable, and are saved as the invitee's own answers on "Next".
 */
export default function CreateWorkspaceWizard({
    steps = ONBOARDING_STEPS,
    initialName = '',
    initialCompany = '',
    initialCountry = '',
    initialStep = 0,
    initialProfile = {},
    defaultProfile = {},
    initialGoals = [],
    initialAudience = [],
    initialHeardAbout = '',
    initialTermsAccepted = false,
    existingWorkspace = null,
    onCreated,
    cropLogo = cropImageToSquare,
}) {
    const [step, setStep] = useState(() => clampStep(initialStep, steps));
    const [fullName, setFullName] = useState(initialName);
    const [company, setCompany] = useState(
        () => cleanAnswer(initialProfile?.company) || cleanAnswer(defaultProfile?.company) || initialCompany,
    );
    const [country, setCountry] = useState(countryName(initialCountry) ? String(initialCountry).toUpperCase() : '');
    // Which of role / company size / industry this card asks is decided once,
    // at mount: a value saved on "Next" must not hide the field on "Back".
    const [profileSelects] = useState(() => missingProfileSelects(initialProfile));
    const [profile, setProfile] = useState(() =>
        Object.fromEntries(
            PROFILE_SELECT_FIELDS.map((field) => [
                field.key,
                knownOption(field, initialProfile?.[field.key]) || knownOption(field, defaultProfile?.[field.key]),
            ]),
        ),
    );
    const [heardAbout, setHeardAbout] = useState(() =>
        HEARD_ABOUT_OPTIONS.includes(initialHeardAbout) ? initialHeardAbout : '',
    );
    const [workspaceName, setWorkspaceName] = useState(existingWorkspace?.name || '');
    const [logo, setLogo] = useState(existingWorkspace?.logo || null);
    const [goals, setGoals] = useState(() => initialGoals.filter((value) => IMPROVE_GOAL_OPTIONS.includes(value)));
    const [audience, setAudience] = useState(() =>
        initialAudience.filter((value) => MODEL_AUDIENCE_OPTIONS.includes(value)),
    );
    const [agreed, setAgreed] = useState(Boolean(initialTermsAccepted));
    const [consents, setConsents] = useState(() =>
        Object.fromEntries(CONSENT_ITEMS.map((item) => [item.id, Boolean(initialTermsAccepted)])),
    );
    const [createdWorkspace, setCreatedWorkspace] = useState(existingWorkspace);
    const [fieldErrors, setFieldErrors] = useState({});
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const termsScrollRef = useRef(null);

    const current = steps[step];
    const titleId = `onboarding-step-${current.id}-title`;
    const hasWorkspace = Boolean(createdWorkspace);
    const allConsented = CONSENT_ITEMS.every((item) => consents[item.id]);
    const progressSegments = progressSegmentsFor(steps);
    const progressPosition = progressSegments.findIndex((segment) => segment.id === current.id) + 1;

    const clearErrors = () => {
        if (Object.keys(fieldErrors).length) setFieldErrors({});
        if (error) setError(null);
    };

    const goBack = () => {
        clearErrors();
        setStep((prev) => Math.max(prev - 1, 0));
    };

    const advance = () => {
        clearErrors();
        setStep((prev) => Math.min(prev + 1, steps.length - 1));
    };

    const save = async (fields) => {
        const result = await updateProfile(fields);
        if (!result.ok) setError(result.error);
        return result.ok;
    };

    const submitAbout = async () => {
        const name = fullName.trim();
        const companyName = company.trim();
        const errors = {};
        if (!name) errors.fullName = 'Please enter your full name.';
        for (const field of profileSelects) {
            if (!profile[field.key]) errors[field.key] = field.error;
        }
        if (!companyName) errors.company = 'Please enter your company name.';
        if (!country) errors.country = 'Please select your country.';
        if (Object.keys(errors).length) {
            setFieldErrors(errors);
            return;
        }
        clearErrors();
        setSubmitting(true);
        const fields = { name, country, company: companyName, completed_step: completedStepFor('about') };
        for (const field of profileSelects) fields[field.key] = profile[field.key];
        const ok = await save(fields);
        setSubmitting(false);
        if (ok) advance();
    };

    const submitWorkspace = async () => {
        // Dashes a trailing space left while typing are not part of the name.
        const name = sanitizeWorkspaceName(workspaceName, { trimEnd: true }).trim();
        if (!name) {
            setFieldErrors({ workspaceName: 'Please enter a workspace name.' });
            return;
        }
        clearErrors();
        setSubmitting(true);
        if (!(await save({ completed_step: completedStepFor('workspace') }))) {
            setSubmitting(false);
            return;
        }
        if (!hasWorkspace) {
            const result = await createWorkspace({ name, company, logo });
            if (!result.ok) {
                setSubmitting(false);
                // A taken name is fixed in the field itself; anything else is a
                // step-level failure.
                if (result.nameTaken) setFieldErrors({ workspaceName: result.error });
                else setError(result.error);
                return;
            }
            setCreatedWorkspace(result.data?.workspace ?? { name });
        }
        setSubmitting(false);
        advance();
    };

    const submitGoals = async () => {
        const errors = {};
        if (!goals.length) errors.improve_goals = 'Please pick at least one option.';
        if (!audience.length) errors.model_audience = 'Please pick at least one option.';
        if (Object.keys(errors).length) {
            setFieldErrors(errors);
            return;
        }
        clearErrors();
        setSubmitting(true);
        const ok = await save({ improve_goals: goals, model_audience: audience, completed_step: completedStepFor('goals') });
        setSubmitting(false);
        if (ok) advance();
    };

    const submitHeardAbout = async () => {
        if (!heardAbout) {
            setFieldErrors({ heard_about: 'Please pick one option.' });
            return;
        }
        clearErrors();
        setSubmitting(true);
        const ok = await save({ heard_about: heardAbout, completed_step: completedStepFor('heard_about') });
        setSubmitting(false);
        if (ok) advance();
    };

    const toggleConsent = (id) => {
        if (submitting) return;
        clearErrors();
        setConsents((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    /**
     * "Continue to Specific": record the agreement once (`terms_accepted_at`
     * is stamped server-side on `accept_terms`), then hand over.  A failed
     * save keeps the checkboxes ticked so the user can simply retry.
     */
    const finish = async () => {
        if (!allConsented || submitting) return;
        if (!agreed) {
            clearErrors();
            setSubmitting(true);
            const ok = await save({ accept_terms: true, completed_step: completedStepFor('terms') });
            setSubmitting(false);
            if (!ok) return;
            setAgreed(true);
        }
        onCreated?.(createdWorkspace ?? null);
    };

    // Picked file → crop dialog → `setLogo(dataUrl)`; a failure shows the
    // step-level error and keeps whatever logo was already chosen.
    const logoCrop = useLogoCrop({
        onLogo: (dataUrl) => {
            setError(null);
            setLogo(dataUrl);
        },
        onError: (err) => setError(logoImageErrorText(err)),
    });

    const nextHandlers = {
        about: submitAbout,
        workspace: submitWorkspace,
        goals: submitGoals,
        heard_about: submitHeardAbout,
    };

    // `undefined` (not '') lets the select fall back to its placeholder.
    const countryLabel = countryName(country) || undefined;

    const fieldError = (key) =>
        fieldErrors[key] ? (
            <Text as="p" variant="detail-regular" className="onboarding-error" role="alert">
                {fieldErrors[key]}
            </Text>
        ) : null;

    // Role / Company Size / Industry (Figma 9358:1189): a plain single-select
    // with the option labels, no prefix icon, error line underneath.
    const renderProfileSelect = (field) => (
        <div key={field.key} className="onboarding-fields">
            <DropdownMenu
                size="medium"
                fill
                selectType="single-select"
                label={field.label}
                showAsterisk
                placeholder={field.placeholder}
                items={field.options.map((option) => ({ label: option }))}
                value={profile[field.key] || undefined}
                disabled={submitting}
                onSelect={(item) => {
                    const value = field.options.includes(item?.label) ? item.label : '';
                    setProfile((prev) => ({ ...prev, [field.key]: value }));
                    clearErrors();
                }}
            />
            {fieldError(field.key)}
        </div>
    );

    const selectFor = (key) => profileSelects.find((field) => field.key === key);

    const renderAbout = () => (
        <div className="onboarding-fields">
            <Input
                id="onboarding-full-name"
                size="large"
                label="Full Name"
                showAsterisk
                name="name"
                autoComplete="name"
                placeholder="Full Name"
                value={fullName}
                danger={Boolean(fieldErrors.fullName)}
                showHelperText={Boolean(fieldErrors.fullName)}
                helperText={fieldErrors.fullName || ''}
                disabled={submitting}
                onChange={(event) => {
                    setFullName(event.target.value);
                    clearErrors();
                }}
            />
            {selectFor('role') ? renderProfileSelect(selectFor('role')) : null}
            <Input
                id="onboarding-company"
                size="large"
                label="Company Name"
                showAsterisk
                name="organization"
                autoComplete="organization"
                placeholder="Your company"
                value={company}
                danger={Boolean(fieldErrors.company)}
                showHelperText={Boolean(fieldErrors.company)}
                helperText={fieldErrors.company || ''}
                disabled={submitting}
                onChange={(event) => {
                    setCompany(event.target.value);
                    clearErrors();
                }}
            />
            {selectFor('company_size') ? renderProfileSelect(selectFor('company_size')) : null}
            {selectFor('industry') ? renderProfileSelect(selectFor('industry')) : null}
            <div className="onboarding-fields">
                <DropdownMenu
                    size="medium"
                    fill
                    selectType="single-select"
                    label="From"
                    showAsterisk
                    placeholder="Select Country"
                    showPrefixIcon
                    prefixIcon={<SparkIcon slug={COUNTRY_ICON_SLUG} size="small" />}
                    searchable
                    searchMode="trigger"
                    searchPlaceholder="Search countries"
                    searchEmptyText="No matching country"
                    items={COUNTRY_ITEMS}
                    value={countryLabel}
                    disabled={submitting}
                    onSelect={(item) => {
                        setCountry(countryCodeForName(item?.label) || '');
                        clearErrors();
                    }}
                />
                {fieldError('country')}
            </div>
        </div>
    );

    const renderWorkspace = () => (
        <>
            <WorkspaceDetailsFields
                idPrefix="onboarding"
                logo={logo}
                onLogoFile={logoCrop.pickLogo}
                name={workspaceName}
                onNameChange={(value) => {
                    setWorkspaceName(value);
                    clearErrors();
                }}
                nameError={fieldErrors.workspaceName || ''}
                nameHelperText={hasWorkspace ? 'Your workspace is already set up.' : ''}
                disabled={submitting || hasWorkspace}
            />
            <LogoCropModal {...logoCrop.modalProps} cropLogo={cropLogo} />
        </>
    );

    // A column of Spark choice tiles (Figma 9237:3371), labelled by the card
    // title or by the group's own heading, with its error line underneath.
    // Single-select tiles are radios, so their wrapper is a radiogroup.
    const renderTiles = ({ options, selectionMode, isSelected, onPick, errorKey, labelledBy }) => (
        <div className="onboarding-fields">
            <div
                className="onboarding-options"
                role={selectionMode === 'single' ? 'radiogroup' : 'group'}
                aria-labelledby={labelledBy}
            >
                {options.map((option) => (
                    <ChoiceTile
                        key={option}
                        label={option}
                        selectionMode={selectionMode}
                        selected={isSelected(option)}
                        disabled={submitting}
                        onClick={() => {
                            onPick(option);
                            clearErrors();
                        }}
                    />
                ))}
            </div>
            {fieldError(errorKey)}
        </div>
    );

    // "What do you want to improve" + "Who is the model for?" share one card
    // (Figma 9224:4431): the second group has its own heading-4 title.
    const audienceTitleId = 'onboarding-step-goals-audience-title';
    const renderGoals = () => (
        <div className="onboarding-choice-groups">
            {renderTiles({
                options: IMPROVE_GOAL_OPTIONS,
                selectionMode: 'multiple',
                isSelected: (option) => goals.includes(option),
                onPick: (option) => setGoals((prev) => toggleValue(prev, option)),
                errorKey: 'improve_goals',
                labelledBy: titleId,
            })}
            <div className="onboarding-choice-group">
                <Text as="h2" id={audienceTitleId} variant="heading-4-semibold" color="primary">
                    {MODEL_AUDIENCE_TITLE}
                </Text>
                {renderTiles({
                    options: MODEL_AUDIENCE_OPTIONS,
                    selectionMode: 'multiple',
                    isSelected: (option) => audience.includes(option),
                    onPick: (option) => setAudience((prev) => toggleValue(prev, option)),
                    errorKey: 'model_audience',
                    labelledBy: audienceTitleId,
                })}
            </div>
        </div>
    );

    // "How did you hear about us?" (Figma 9226:4449): one answer, radio-like
    // tiles — picking another option replaces the previous one.
    const renderHeardAbout = () =>
        renderTiles({
            options: HEARD_ABOUT_OPTIONS,
            selectionMode: 'single',
            isSelected: (option) => heardAbout === option,
            onPick: setHeardAbout,
            errorKey: 'heard_about',
            labelledBy: titleId,
        });

    const renderTerms = () => (
        <div className="onboarding-terms">
            <div className="onboarding-terms-box">
                <div
                    ref={termsScrollRef}
                    className="onboarding-terms-scroll"
                    tabIndex={0}
                    role="region"
                    aria-label="Terms and conditions"
                >
                    <Text as="p" variant="base-regular" color="primary" className="onboarding-terms-text">
                        {ONBOARDING_TERMS_TEXT}
                    </Text>
                </div>
                <ScrollBar containerRef={termsScrollRef} />
            </div>
            <div className="onboarding-terms-consent">
                {CONSENT_ITEMS.map((item) => (
                    <ConsentCheckbox
                        key={item.id}
                        item={item}
                        checked={Boolean(consents[item.id])}
                        disabled={submitting || agreed}
                        onToggle={() => toggleConsent(item.id)}
                    />
                ))}
            </div>
        </div>
    );

    const renderBody = () => {
        switch (current.id) {
            case 'about':
                return renderAbout();
            case 'workspace':
                return renderWorkspace();
            case 'goals':
                return renderGoals();
            case 'heard_about':
                return renderHeardAbout();
            default:
                return renderTerms();
        }
    };

    const footer =
        current.id === 'terms' ? (
            <Button
                size="large"
                variant="primary"
                disabled={!allConsented || submitting}
                aria-busy={submitting || undefined}
                onClick={finish}
            >
                Continue to Specific
            </Button>
        ) : (
            <Button
                size="large"
                variant="primary"
                disabled={submitting}
                aria-busy={submitting || undefined}
                onClick={nextHandlers[current.id]}
            >
                Next
            </Button>
        );

    return (
        <OnboardingLayout
            variant={current.variant || 'split'}
            artwork={current.artwork}
            cardClassName={current.id === 'goals' ? 'onboarding-card-goals' : ''}
            footer={footer}
            progress={
                current.id === 'terms' ? null : (
                    <OnboardingProgress segments={progressSegments} position={progressPosition} />
                )
            }
        >
            <div className="onboarding-header">
                {step > 0 ? (
                    <Button
                        size="tiny"
                        variant="ghost"
                        aria-label="Back"
                        tooltip="Back"
                        disabled={submitting}
                        startIcon={<SparkIcon slug="arrow-left" size="medium" />}
                        onClick={goBack}
                    />
                ) : null}
                <Text as="h1" id={titleId} variant="heading-4-semibold" color="primary">
                    {current.title}
                </Text>
                {current.subtitle ? (
                    <Text as="p" variant="lead-regular" color="primary">
                        {current.subtitle}
                    </Text>
                ) : null}
            </div>
            {renderBody()}
            {error ? (
                <Text as="p" variant="base-regular" role="alert" className="onboarding-error">
                    {error}
                </Text>
            ) : null}
        </OnboardingLayout>
    );
}
